import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  FileSpreadsheet, 
  Settings, 
  LogOut, 
  DollarSign,
  Clock,
  MoreVertical,
  Trash2,
  Download,
  Link,
  Unlink,
  Loader2,
  Menu,
  X,
  FileText,
  Building2,
  Receipt,
  BarChart3,
  Calculator,
  TrendingUp,
  Archive,
  CloudSun,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { InvoicingPage } from './InvoicingPage';
import { BusinessSettingsPage } from './BusinessSettingsPage';
import { ReportSection } from '../components/ReportSection';
import { PaymentDelayRecap } from '../components/PaymentDelayRecap';
import { ThemeToggle } from '../components/ThemeToggle';
import { ChargesPage } from './ChargesPage';
import { CalculatorPage } from './CalculatorPage';
import { ProfitPage } from './ProfitPage';
import { ArchivePage } from './ArchivePage';
import { PrevisionnelPage } from './PrevisionnelPage';
import { MySubscriptionPage } from './MySubscriptionPage';
import { ImpotsPage } from './ImpotsPage';

export const DashboardPage = () => {
  const { user, logout, api } = useAuth();
  const disabledFeatures = (user && user.disabled_features) || [];
  const isFeatureDisabled = (id) => disabledFeatures.indexOf(id) !== -1;
  const [currentPage, setCurrentPage] = useState('calendar');
  const [appointments, setAppointments] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  
  // Dialog states
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  // Form states
  const [appointmentForm, setAppointmentForm] = useState({
    client_type: 'Particulier',
    client_name: '',
    service_type: '',
    price: '',
    date: '',
    time: '',
    duration_minutes: 60,
    payment_method: 'CB',
    provider: '',
    payment_delay_month: '',
    notes: ''
  });
  const [newService, setNewService] = useState({ name: '', default_price: '', default_duration: 60 });
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // Get unique client names from appointments
  const uniqueClientNames = useMemo(() => {
    const names = [...new Set(appointments.map(apt => apt.client_name))];
    return names.sort();
  }, [appointments]);

  // Filter client suggestions based on input
  const filterClientSuggestions = (value) => {
    if (!value) {
      setClientSuggestions([]);
      return;
    }
    const filtered = uniqueClientNames.filter(name => 
      name.toLowerCase().includes(value.toLowerCase())
    );
    setClientSuggestions(filtered);
  };

  const handleClientNameChange = (value) => {
    setAppointmentForm({ ...appointmentForm, client_name: value });
    filterClientSuggestions(value);
    setShowClientSuggestions(true);
  };

  const selectClientSuggestion = (name) => {
    setAppointmentForm({ ...appointmentForm, client_name: name });
    setShowClientSuggestions(false);
    setClientSuggestions([]);
  };

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchAppointments();
    }
  }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAppointments(),
        fetchServiceTypes(),
        fetchStats(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const start = startOfMonth(currentMonth).toISOString();
      const end = endOfMonth(currentMonth).toISOString();
      const response = await api.get(`/appointments?start_date=${start}&end_date=${end}`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/service-types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCalendarStatus = async () => {};

  // Appointment handlers
  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!appointmentForm.client_name || !appointmentForm.service_type || !appointmentForm.price || !appointmentForm.date || !appointmentForm.time) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    try {
      const dateTime = new Date(`${appointmentForm.date}T${appointmentForm.time}`);
      const payload = {
        client_type: appointmentForm.client_type,
        client_name: appointmentForm.client_name,
        service_type: appointmentForm.service_type,
        price: parseFloat(appointmentForm.price),
        date: dateTime.toISOString(),
        duration_minutes: parseInt(appointmentForm.duration_minutes),
        payment_method: appointmentForm.payment_method,
        provider: appointmentForm.provider || null,
        payment_delay_month: appointmentForm.payment_delay_month || null,
        notes: appointmentForm.notes || null
      };
      
      if (editingAppointment) {
        await api.put(`/appointments/${editingAppointment.id}`, payload);
        toast.success('Rendez-vous mis à jour');
      } else {
        await api.post('/appointments', payload);
        toast.success('Rendez-vous créé');
      }
      
      setAppointmentDialogOpen(false);
      resetAppointmentForm();
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success('Rendez-vous supprimé');
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditAppointment = (apt) => {
    const date = parseISO(apt.date);
    setEditingAppointment(apt);
    setAppointmentForm({
      client_type: apt.client_type || 'Particulier',
      client_name: apt.client_name,
      service_type: apt.service_type,
      price: apt.price.toString(),
      date: format(date, 'yyyy-MM-dd'),
      time: format(date, 'HH:mm'),
      duration_minutes: apt.duration_minutes,
      payment_method: apt.payment_method || 'CB',
      provider: apt.provider || '',
      payment_delay_month: apt.payment_delay_month || '',
      notes: apt.notes || ''
    });
    setAppointmentDialogOpen(true);
  };

  const handleCreateInvoiceFromAppointment = async (appointmentId) => {
    try {
      await api.post(`/appointments/${appointmentId}/create-invoice`);
      toast.success('Facture créée');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la facture');
    }
  };

  const resetAppointmentForm = () => {
    setAppointmentForm({
      client_type: 'Particulier',
      client_name: '',
      service_type: '',
      price: '',
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: '09:00',
      duration_minutes: 60,
      payment_method: 'CB',
      provider: '',
      payment_delay_month: '',
      notes: ''
    });
    setEditingAppointment(null);
  };

  // Service type handlers
  const handleCreateService = async (e) => {
    e.preventDefault();
    if (!newService.name || !newService.default_price) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      await api.post('/service-types', {
        name: newService.name,
        default_price: parseFloat(newService.default_price),
        default_duration: parseInt(newService.default_duration)
      });
      toast.success('Type de service créé');
      setServiceDialogOpen(false);
      setNewService({ name: '', default_price: '', default_duration: 60 });
      fetchServiceTypes();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteService = async (id) => {
    try {
      await api.delete(`/service-types/${id}`);
      toast.success('Type de service supprimé');
      fetchServiceTypes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Export handlers
  const handleExportExcel = async () => {
    setExportExcelLoading(true);
    try {
      const response = await api.get('/export/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointments_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Fichier Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleExportPdfDetailed = async () => {
    setExportPdfLoading(true);
    try {
      const response = await api.get('/export/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointments_detail_${format(new Date(), 'yyyyMMdd')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF détaillé téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF détaillé');
    } finally {
      setExportPdfLoading(false);
    }
  };

  // Calendar connection handlers — moved to "Mon entreprise" page (CalendarIntegrations component).

  // Get appointments for selected date
  const selectedDateAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.date);
    return isSameDay(aptDate, selectedDate);
  });

  // Helper: format payment_delay_month "YYYY-MM" → "mois année"
  const formatDelayMonth = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-');
    const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Helper: an appointment counts as revenue on its prestation day ONLY if no payment delay is set
  const apptCountsAsRevenueToday = (apt) => !apt.payment_delay_month;

  const selectedDateStats = useMemo(() => {
    let revenue = 0;
    for (let i = 0; i < selectedDateAppointments.length; i += 1) {
      const apt = selectedDateAppointments[i];
      if (apptCountsAsRevenueToday(apt)) {
        revenue += Number(apt.price || 0);
      }
    }

    return {
      appointmentsCount: selectedDateAppointments.length,
      revenue: Number(revenue.toFixed(2))
    };
  }, [selectedDateAppointments]);

  // Get dates with appointments for calendar
  const datesWithAppointments = appointments.map(apt => parseISO(apt.date));

  const handleServiceSelect = (serviceName) => {
    const service = serviceTypes.find(s => s.name === serviceName);
    if (service) {
      setAppointmentForm({
        ...appointmentForm,
        service_type: serviceName,
        price: service.default_price.toString(),
        duration_minutes: service.default_duration
      });
    } else {
      setAppointmentForm({ ...appointmentForm, service_type: serviceName });
    }
  };

  const openNewAppointment = () => {
    resetAppointmentForm();
    setAppointmentForm(prev => ({
      ...prev,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: '09:00'
    }));
    setAppointmentDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="dashboard">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed md:static w-64 h-screen bg-white border-r border-slate-200 flex flex-col z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-7 h-7 text-[#065F46]" />
              <span className="font-outfit font-bold text-xl tracking-tight text-[#065F46]">Ledgerly</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          <button
            onClick={() => { setCurrentPage('calendar'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'calendar' 
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-calendar"
            style={{ display: isFeatureDisabled('calendar') ? 'none' : undefined }}
          >
            <CalendarIcon className="w-5 h-5" />
            <span className="font-medium">Calendrier</span>
          </button>
          <button
            onClick={() => { setCurrentPage('reports'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'reports'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-reports"
            style={{ display: isFeatureDisabled('reports') ? 'none' : undefined }}
          >
            <BarChart3 className="w-5 h-5" />
            <span className="font-medium">Compte rendu</span>
          </button>
          <button
            onClick={() => { setCurrentPage('charges'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'charges'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-charges"
            style={{ display: isFeatureDisabled('charges') ? 'none' : undefined }}
          >
            <DollarSign className="w-5 h-5" />
            <span className="font-medium">Charge</span>
          </button>
          <button
            onClick={() => { setCurrentPage('profit'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'profit'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-profit"
            style={{ display: isFeatureDisabled('profit') ? 'none' : undefined }}
          >
            <TrendingUp className="w-5 h-5" />
            <span className="font-medium">Bénéfice</span>
          </button>
          <button
            onClick={() => { setCurrentPage('forecast'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'forecast'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-forecast"
            style={{ display: isFeatureDisabled('forecast') ? 'none' : undefined }}
          >
            <CloudSun className="w-5 h-5" />
            <span className="font-medium">Prévisionnel</span>
          </button>
          <button
            onClick={() => { setCurrentPage('impots'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'impots'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-impots"
            style={{ display: isFeatureDisabled('impots') ? 'none' : undefined }}
          >
            <Receipt className="w-5 h-5" />
            <span className="font-medium">Impôts</span>
          </button>
          <button
            onClick={() => { setCurrentPage('archive'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'archive'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-archive"
            style={{ display: isFeatureDisabled('archive') ? 'none' : undefined }}
          >
            <Archive className="w-5 h-5" />
            <span className="font-medium">Archive</span>
          </button>
          <button
            onClick={() => { setCurrentPage('invoicing'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'invoicing' 
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-invoicing"
            style={{ display: isFeatureDisabled('invoicing') ? 'none' : undefined }}
          >
            <FileText className="w-5 h-5" />
            <span className="font-medium">Devis & Factures</span>
          </button>
          <button
            onClick={() => { setCurrentPage('calculator'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'calculator'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-calculator"
            style={{ display: isFeatureDisabled('calculator') ? 'none' : undefined }}
          >
            <Calculator className="w-5 h-5" />
            <span className="font-medium">Calculatrice</span>
          </button>
          <button
            onClick={() => { setCurrentPage('subscription'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'subscription'
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-subscription"
          >
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">Mon abonnement</span>
          </button>
          <button
            onClick={() => { setCurrentPage('business'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              currentPage === 'business' 
                ? 'bg-[#065F46]/10 text-[#065F46] border-l-3 border-[#065F46]' 
                : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="nav-business"
            style={{ display: isFeatureDisabled('business') ? 'none' : undefined }}
          >
            <Building2 className="w-5 h-5" />
            <span className="font-medium">Mon entreprise</span>
          </button>
        </nav>
        
        <div className="p-4 border-t border-slate-100">
          {user?.subscription_status === 'TRIAL' && user?.trial_ends_at && (() => {
            const daysLeft = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000));
            return (
              <div className="mb-3 p-3 rounded-md bg-sky-50 border border-sky-200" data-testid="trial-banner">
                <p className="text-xs font-medium text-sky-900">Essai gratuit</p>
                <p className="text-xs text-sky-700 mt-0.5">{daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}</p>
                <button
                  onClick={() => { window.location.href = '/subscription'; }}
                  className="mt-2 w-full text-xs font-medium bg-[#065F46] hover:bg-[#047857] text-white py-1.5 rounded transition-colors"
                  data-testid="upgrade-btn"
                >
                  S'abonner
                </button>
              </div>
            );
          })()}
          {user?.subscription_status === 'ACTIVE' && (
            <div className="mb-3 p-3 rounded-md bg-emerald-50 border border-emerald-200" data-testid="sub-active-banner">
              <p className="text-xs font-medium text-emerald-900">Abonnement actif</p>
              <p className="text-xs text-emerald-700 mt-0.5">{user.subscription_plan === 'yearly' ? 'Annuel' : 'Mensuel'}</p>
            </div>
          )}
          {user?.subscription_status === 'UNLIMITED' && (
            <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="sub-unlimited-banner">
              <p className="text-xs font-medium text-amber-900">Accès illimité</p>
              <p className="text-xs text-amber-700 mt-0.5">Offert par l'admin</p>
            </div>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#065F46] flex items-center justify-center text-white font-medium">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.email}</p>
              <p className="text-xs text-slate-500">Propriétaire</p>
            </div>
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-btn">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSettingsDialogOpen(true)} data-testid="settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" /> Paramètres
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 mb-6 md:hidden">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>

        {currentPage === 'calendar' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900">Calendrier</h1>
                <p className="text-slate-500 text-sm md:text-base">{format(new Date(), 'EEEE d MMMM yyyy')}</p>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <Button 
                  onClick={handleExportExcel}
                  className="bg-[#D97706] hover:bg-[#B45309] text-white btn-press hidden md:flex"
                  data-testid="export-excel-btn"
                  disabled={exportExcelLoading}
                >
                  {exportExcelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  Export Excel
                </Button>
                <Button 
                  onClick={handleExportPdfDetailed}
                  className="bg-[#065F46] hover:bg-[#047857] text-white btn-press hidden md:flex"
                  data-testid="export-pdf-btn"
                  disabled={exportPdfLoading}
                >
                  {exportPdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  PDF détaillé
                </Button>
                <Button 
                  onClick={handleExportExcel}
                  className="bg-[#D97706] hover:bg-[#B45309] text-white btn-press md:hidden"
                  size="icon"
                  data-testid="export-excel-btn-mobile"
                  disabled={exportExcelLoading}
                >
                  {exportExcelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={handleExportPdfDetailed}
                  className="bg-[#065F46] hover:bg-[#047857] text-white btn-press md:hidden"
                  size="icon"
                  data-testid="export-pdf-btn-mobile"
                  disabled={exportPdfLoading}
                >
                  {exportPdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                </Button>
                <Button 
                  onClick={openNewAppointment}
                  className="bg-[#065F46] hover:bg-[#047857] text-white btn-press"
                  data-testid="new-appointment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <span className="hidden md:inline">Nouveau RDV</span>
                  <span className="md:hidden">Nouveau</span>
                </Button>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 mb-8">
              <Card className="bg-white border border-slate-200 shadow-sm stats-card" data-testid="stat-today-appointments">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-slate-500 font-medium" data-testid="selected-date-appointments-label">RDV du jour sélectionné</p>
                      <p className="text-xl md:text-3xl font-outfit font-bold text-slate-900 mt-1" data-testid="selected-date-appointments-value">{selectedDateStats.appointmentsCount}</p>
                    </div>
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[#065F46]/10 flex items-center justify-center">
                      <CalendarIcon className="w-5 h-5 md:w-6 md:h-6 text-[#065F46]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-slate-200 shadow-sm stats-card" data-testid="stat-today-revenue">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-slate-500 font-medium" data-testid="selected-date-revenue-label">CA du jour sélectionné</p>
                      <p className="text-xl md:text-3xl font-mono font-bold text-[#065F46] mt-1" data-testid="selected-date-revenue-value">{selectedDateStats.revenue.toFixed(2)} €</p>
                    </div>
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[#D97706]/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-[#D97706]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Main Grid */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="lg:col-span-1 bg-white border border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-outfit text-lg">Calendrier</CardTitle>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                        data-testid="calendar-prev-month"
                      >
                        ←
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                        data-testid="calendar-next-month"
                      >
                        →
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (!date) return;
                      setSelectedDate(date);
                      setCurrentMonth(date);
                    }}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    className="rounded-md"
                    modifiers={{
                      hasAppointment: datesWithAppointments
                    }}
                    modifiersStyles={{
                      hasAppointment: {
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                        textDecorationColor: '#065F46'
                      }
                    }}
                    data-testid="calendar"
                  />
                </CardContent>
              </Card>
              
              {/* Appointments List */}
              <Card className="lg:col-span-2 bg-white border border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-outfit text-lg">
                      RDV du {format(selectedDate, 'd MMMM yyyy')}
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={openNewAppointment}
                      data-testid="add-appointment-card-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {selectedDateAppointments.length === 0 ? (
                    <div className="text-center py-12">
                      <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Aucun rendez-vous pour cette date</p>
                      <Button 
                        className="mt-4 bg-[#065F46] hover:bg-[#047857] text-white"
                        onClick={openNewAppointment}
                        data-testid="empty-state-add-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Planifier un RDV
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateAppointments.map((apt, index) => (
                        <div 
                          key={`${apt.id}-${index}`} 
                          className="appointment-item p-4 bg-white border border-slate-200 rounded-md hover:shadow-sm transition-shadow stagger-item"
                          data-testid={`appointment-item-${index}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`px-2 py-0.5 rounded text-xs ${
                                    apt.client_type === 'Professionnel'
                                      ? 'bg-indigo-100 text-indigo-700'
                                      : apt.client_type === 'Autre'
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-slate-100 text-slate-700'
                                  }`}
                                  data-testid={`appointment-client-type-${index}`}
                                >
                                  {apt.client_type || 'Particulier'}
                                </span>
                                <span className="font-medium text-slate-900">{apt.client_name}</span>
                                <span className="service-badge">{apt.service_type}</span>
                                {apt.invoice_id && (
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">Facturé</span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(parseISO(apt.date), 'HH:mm')}
                                </span>
                                <span className="font-mono text-[#065F46] font-medium">
                                  {apt.price.toFixed(2)} €
                                </span>
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                  {apt.payment_method || 'CB'}
                                </span>
                                {apt.provider && (
                                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs" data-testid={`apt-provider-${index}`}>
                                    👤 {apt.provider}
                                  </span>
                                )}
                                {apt.payment_delay_month && (
                                  <span
                                    className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-xs flex items-center gap-1"
                                    data-testid={`apt-payment-delay-badge-${index}`}
                                    title="Le montant est comptabilisé sur le mois choisi (et non sur ce jour)"
                                  >
                                    ⏳ Paiement reporté → {formatDelayMonth(apt.payment_delay_month)}
                                  </span>
                                )}
                              </div>
                              {apt.notes && (
                                <p className="text-xs text-slate-400 mt-2">{apt.notes}</p>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`appointment-menu-${index}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditAppointment(apt)}>
                                  <Settings className="w-4 h-4 mr-2" /> Modifier
                                </DropdownMenuItem>
                                {!apt.invoice_id && (
                                  <DropdownMenuItem onClick={() => handleCreateInvoiceFromAppointment(apt.id)}>
                                    <Receipt className="w-4 h-4 mr-2" /> Créer facture
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteAppointment(apt.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <PaymentDelayRecap appointments={appointments} />
            </div>
          </>
        )}

        {currentPage === 'reports' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="reports-page-title">Compte rendu</h1>
                <p className="text-slate-500 text-sm md:text-base" data-testid="reports-page-subtitle">Synthèse hebdomadaire, mensuelle et annuelle</p>
              </div>
            </div>

            <Card className="max-w-5xl bg-white border border-slate-200 shadow-sm" data-testid="reports-page-card">
              <ReportSection />
            </Card>
          </>
        )}

        {currentPage === 'calculator' && <CalculatorPage />}
        {currentPage === 'charges' && <ChargesPage />}
        {currentPage === 'profit' && <ProfitPage />}
        {currentPage === 'forecast' && <PrevisionnelPage />}
        {currentPage === 'archive' && <ArchivePage />}
        {currentPage === 'impots' && <ImpotsPage />}

        {currentPage === 'invoicing' && <InvoicingPage />}
        {currentPage === 'subscription' && <MySubscriptionPage />}
        {currentPage === 'business' && <BusinessSettingsPage />}
      </main>
      
      {/* New/Edit Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="appointment-dialog-content">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingAppointment ? 'Modifier le RDV' : 'Nouveau RDV'}
            </DialogTitle>
            <DialogDescription>
              {editingAppointment ? 'Modifiez les informations du rendez-vous.' : 'Remplissez les informations du rendez-vous.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client_type">Type de client *</Label>
              <Select
                value={appointmentForm.client_type}
                onValueChange={(value) => setAppointmentForm({ ...appointmentForm, client_type: value })}
              >
                <SelectTrigger id="client_type" className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]" data-testid="appointment-client-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Particulier">Particulier</SelectItem>
                  <SelectItem value="Professionnel">Professionnel</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">Nom du client *</Label>
              <div className="relative">
                <Input
                  id="client_name"
                  value={appointmentForm.client_name}
                  onChange={(e) => handleClientNameChange(e.target.value)}
                  onFocus={() => {
                    filterClientSuggestions(appointmentForm.client_name);
                    setShowClientSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                  placeholder="Jean Dupont"
                  className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  autoComplete="off"
                  data-testid="appointment-client-input"
                />
                {showClientSuggestions && clientSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                    {clientSuggestions.map((name, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectClientSuggestion(name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="service_type">Type de service *</Label>
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm" 
                  onClick={() => setServiceDialogOpen(true)}
                  className="text-[#065F46] h-auto p-0"
                >
                  + Ajouter
                </Button>
              </div>
              <Select 
                value={appointmentForm.service_type} 
                onValueChange={handleServiceSelect}
              >
                <SelectTrigger data-testid="appointment-service-select">
                  <SelectValue placeholder="Sélectionner un service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((service, idx) => (
                    <SelectItem key={`${service.id}-${idx}`} value={service.name}>
                      {service.name} ({service.default_price} €)
                    </SelectItem>
                  ))}
                  {serviceTypes.length === 0 && (
                    <SelectItem value="_none" disabled>Aucun service - créez-en un</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Prix (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={appointmentForm.price}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, price: e.target.value })}
                  placeholder="0.00"
                  className="font-mono border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  data-testid="appointment-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_method">Mode de paiement</Label>
                <Select 
                  value={appointmentForm.payment_method} 
                  onValueChange={(v) => setAppointmentForm({ ...appointmentForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="appointment-payment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CB">CB</SelectItem>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Chèque">Chèque</SelectItem>
                    <SelectItem value="Espèce">Espèce</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                  className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  data-testid="appointment-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Heure *</Label>
                <Input
                  id="time"
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                  className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  data-testid="appointment-time-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="provider">Prestataire</Label>
              <Input
                id="provider"
                type="text"
                value={appointmentForm.provider}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, provider: e.target.value })}
                placeholder="Nom de l'employé qui a effectué le travail"
                className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                data-testid="appointment-provider-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_delay_month">
                Délai de paiement <span className="text-slate-400 font-normal">(facultatif)</span>
              </Label>
              <Input
                id="payment_delay_month"
                type="month"
                value={appointmentForm.payment_delay_month}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, payment_delay_month: e.target.value })}
                className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                data-testid="appointment-payment-delay-input"
              />
              <p className="text-xs text-slate-400">
                Si renseigné, le montant comptera pour le mois sélectionné dans le CA et le Bénéfice (et non le mois de la prestation).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                placeholder="Notes supplémentaires..."
                className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46] resize-none"
                rows={3}
                data-testid="appointment-notes-input"
              />
            </div>
            
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuler</Button>
              </DialogClose>
              <Button 
                type="submit" 
                className="bg-[#065F46] hover:bg-[#047857] text-white"
                data-testid="appointment-submit-btn"
              >
                {editingAppointment ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* New Service Dialog */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit">Ajouter un service</DialogTitle>
            <DialogDescription>Créez un nouveau type de service avec prix et durée par défaut.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateService} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service_name">Nom du service</Label>
              <Input
                id="service_name"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                placeholder="ex: Consultation"
                className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                data-testid="service-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_price">Prix (€)</Label>
                <Input
                  id="service_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newService.default_price}
                  onChange={(e) => setNewService({ ...newService, default_price: e.target.value })}
                  placeholder="0.00"
                  className="font-mono border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  data-testid="service-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_duration">Durée (min)</Label>
                <Input
                  id="service_duration"
                  type="number"
                  min="15"
                  step="15"
                  value={newService.default_duration}
                  onChange={(e) => setNewService({ ...newService, default_duration: e.target.value })}
                  className="border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                  data-testid="service-duration-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuler</Button>
              </DialogClose>
              <Button 
                type="submit" 
                className="bg-[#065F46] hover:bg-[#047857] text-white"
                data-testid="service-submit-btn"
              >
                Ajouter
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">Paramètres</DialogTitle>
            <DialogDescription>Gérez les intégrations calendrier et types de services.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Calendar integrations now live in "Mon entreprise" */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              📅 Les intégrations calendrier (Google, Outlook…) se gèrent désormais dans la page <span className="font-medium text-slate-900">Mon entreprise</span>.
            </div>

            {/* Service Types */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-slate-900">Types de services</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setServiceDialogOpen(true)}
                  data-testid="add-service-settings-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-auto">
                {serviceTypes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucun type de service</p>
                ) : (
                  serviceTypes.map((service) => (
                    <div 
                      key={service.id} 
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{service.name}</p>
                        <p className="text-sm text-slate-500">
                          <span className="font-mono">{service.default_price} €</span> • {service.default_duration} min
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteService(service.id)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
